/**
 * SchemeLinkBanner - Compact link from a project settings tab back to the
 * central analysis-setup scheme (/analysis-setup/<step>). Project settings
 * remain a valid second entry point to the same editors; this banner makes
 * that relationship visible instead of leaving the two surfaces disconnected.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Workflow } from 'lucide-react';
import { PIPELINE_STEPS, type PipelineStepId } from '../steps';

// From the catalog, like the layout's STEP_BY_PATH: a hand-kept copy needed a
// second edit for every new step.
const STEP_PATHS = Object.fromEntries(
  PIPELINE_STEPS.map((step) => [step.id, step.to])
) as Record<PipelineStepId, string>;

interface SchemeLinkBannerProps {
  step: PipelineStepId;
}

export function SchemeLinkBanner({ step }: SchemeLinkBannerProps) {
  const { t } = useTranslation('analysisSetup');

  return (
    <div className="flex items-center gap-2 rounded-card bg-bg-surface px-3 py-2 text-xs text-fg-muted">
      <Workflow className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{t('partOfScheme')}</span>
      <Link
        to={STEP_PATHS[step]}
        className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        {t('openHub')}
      </Link>
    </div>
  );
}
